import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { useRouteMatch } from 'react-router-dom';
import { Container as DraggableContainer } from 'react-smooth-dnd';
import uuid from 'uuid/v4';
import moment from 'moment';
import format from 'string-template';
import ReactHtmlParser from 'react-html-parser';
import { MenuItem, Alert } from '@mui/material';

import { DateFormat } from '../../../../../utils/date';
import { removeSpaces } from '../../../../../utils/string';
import useLogger from '../../../../../hooks/useLogger';
import { CoachingActivity } from '../../../../../utils/log';
import EmptyView from '../../../../components/EmptyView';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import User from '../../../../../Model/User';
import AppCustomCheckin, { DEFAULT_TAG } from '../../../../Model/AppCustomCheckin';
import useSessionStore from '../../../../../hooks/useSessionStore';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import CustomizationContext, { withCustomizationContextReady } from '../../../../context/CustomizationContext';
import ManageClientContext from '../../../../context/ManageClientContext';
import useToast from '../../../../hooks/useToast';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import CheckInItem from './components/CheckInItem';
import QuestionInputModal from './components/QuestionInputModal';

import texts from './texts.json';
import {
  Container,
  Title,
  TitleContainer,
  StyledButton,
  StyledList,
  HeaderContainer,
  ConfigContainer,
  StyledQuestionIcon,
  SubTitle,
  CheckInDayText,
  CheckInDayLabel,
  CheckInDayContainer,
  CheckInDay,
  ClientCount,
  StyledStarIcon,
  ImageUploadOptionsContainer,
  StyledRadioButtonGroup,
  StyledSelectField,
} from './styles';
import CheckInDayConfirmContent from './components/CheckInDayConfirmContent';

const CheckInCustomization = () => {
  const [selectedTag, setSelectedTag] = useState(DEFAULT_TAG);
  const [selectedCheckinDoc, setSelectedCheckinDoc] = useState(null);
  const [checkInQuestionList, setCheckInQuestionList] = useState([]);
  const [checkInQuestionDefinition, setCheckInQuestionDefinition] = useState({});
  const [newCheckInDay, setNewCheckInDay] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userCountByCheckInDay, setUserCountByCheckInDay] = useState({});
  const [activeUsersCollection, setActiveUsersCollection] = useState();

  const { params: { userId: coachId } } = useRouteMatch();
  const { userId: loggedInUserId } = useSessionStore();
  const { customCheckInCollection } = useContext(CustomizationContext);
  const { coachDoc: { name: coachName } } = useContext(ExternalCoachContext);
  const { clientTagsDocs } = useContext(ManageClientContext);
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const isComponentMountedRef = useComponentMounted();

  const defaultCheckinDoc = useMemo(() => customCheckInCollection.docs.find((doc) => doc.tag === DEFAULT_TAG),
    [customCheckInCollection.docs]);

  const {
    checkInDay: customCheckInDay,
    checkInDayUpdatedBy: checkInDayUpdatedby,
    checkInDayUpdatedAt,
    isImageUploadCompulsory,
  } = defaultCheckinDoc || {};

  const formattedCheckInDayUpdatedAt = moment(checkInDayUpdatedAt.toDate()).format(DateFormat.TEXT_DATE_FORMAT);

  const clientTagsList = useMemo(() => ([
    {
      id: DEFAULT_TAG,
      value: DEFAULT_TAG,
      label: texts.defaultTemplateLabel,
    },
    ...clientTagsDocs.map((doc) => ({
      id: doc.id,
      value: doc.tag,
      label: doc.tag,
    })),
  ]), [clientTagsDocs]);

  // This effect is used to set the selected checkin doc, question list and question definition.
  useEffect(() => {
    const currentCheckinDoc = customCheckInCollection.docs.find((doc) => doc.tag === selectedTag);
    if (currentCheckinDoc) {
      setSelectedCheckinDoc(currentCheckinDoc);
      setCheckInQuestionList(currentCheckinDoc.checkInQuestions);
      setCheckInQuestionDefinition(currentCheckinDoc.checkInQuestionDefinition);
    } else {
      setSelectedCheckinDoc(null);
      setCheckInQuestionList([]);
      setCheckInQuestionDefinition({});
    }
  }, [
    customCheckInCollection,
    customCheckInCollection.docs.length,
    selectedTag,
  ]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const userCollection = await User.getActiveUsersByCoach(coachId);
      if (isComponentMountedRef.current) {
        setActiveUsersCollection(userCollection);
        setIsLoading(false);
      }
    };

    init();
  }, [
    isComponentMountedRef,
    coachId,
  ]);

  // calculate user count by check in day
  useEffect(() => {
    if (activeUsersCollection && activeUsersCollection.docs.length > 0) {
      const checkInDayUserCount = activeUsersCollection.docs.reduce((res, userDoc) => {
        const { checkInDay } = userDoc;
        if (!res[checkInDay]) {
          res[checkInDay] = 0;
        }
        res[checkInDay] += 1;
        return res;
      }, {});
      setUserCountByCheckInDay(checkInDayUserCount);
    }
  }, [
    activeUsersCollection,
  ]);

  const updateCheckInDoc = useCallback(async (updatedCheckInQuestionList, updatedCheckInQuestionDefinition) => {
    setIsLoading(true);

    // If the check-in template is not the default, and the question list is empty, delete the doc.
    if (updatedCheckInQuestionList.length === 0 && selectedTag !== DEFAULT_TAG) {
      selectedCheckinDoc.deleteDoc();
    } else {
      selectedCheckinDoc.setCheckinQuestionDefinition(updatedCheckInQuestionDefinition, loggedInUserId);
      selectedCheckinDoc.setCheckinQuestions(updatedCheckInQuestionList, loggedInUserId);
    }

    logCoachingActivity(CoachingActivity.UPDATED_CHECKIN_SETTINGS);
    if (isComponentMountedRef.current) {
      setIsLoading(false);
      setCheckInQuestionDefinition(updatedCheckInQuestionDefinition);
      setCheckInQuestionList(updatedCheckInQuestionList);
    }
  }, [
    isComponentMountedRef,
    logCoachingActivity,
    selectedCheckinDoc,
    selectedTag,
    loggedInUserId,
  ]);

  const addNewQuestion = useCallback(async (question, prompt) => {
    if (question && prompt) {
      let newCheckInQuestionList = null;
      let newCheckInQuestionDefinition = null;
      let newCheckInQuestionId = uuid();
      let oldCheckInQuestionObject = null;
      let isDuplicate = false;

      if (checkInQuestionDefinition) {
        Object.keys(checkInQuestionDefinition).forEach((checkInQuestion) => {
          if (removeSpaces(checkInQuestionDefinition[checkInQuestion].question) === removeSpaces(question)) {
            oldCheckInQuestionObject = checkInQuestionDefinition[checkInQuestion];
            // bring back the deleted question if there is any by using the old id
            newCheckInQuestionId = oldCheckInQuestionObject.id;

            if (checkInQuestionDefinition[checkInQuestion].active) {
              isDuplicate = true;
            }
          }
        });

        if (isDuplicate) {
          showToast(texts.duplicateCheckin, { warning: true });
          return;
        }
        newCheckInQuestionList = [...checkInQuestionList];
        if (!checkInQuestionList.includes(newCheckInQuestionId)) {
          newCheckInQuestionList.push(newCheckInQuestionId);
        }
      } else {
        newCheckInQuestionList = [newCheckInQuestionId];
      }

      // check new question in the question definition. if present change the active status
      if (checkInQuestionDefinition && oldCheckInQuestionObject) {
        checkInQuestionDefinition[oldCheckInQuestionObject.id].active = true;
        newCheckInQuestionDefinition = { ...checkInQuestionDefinition };
      } else {
        newCheckInQuestionDefinition = {
          ...checkInQuestionDefinition,
          [newCheckInQuestionId]: {
            question,
            prompt,
            id: newCheckInQuestionId,
            active: true,
          },
        };
      }

      if (selectedCheckinDoc) {
        updateCheckInDoc(newCheckInQuestionList, newCheckInQuestionDefinition);
      } else {
        // If the check-in doc is not found, create a new one based on the selected tag.
        setIsLoading(true);
        const newCheckInQuestionTemplate = {
          checkInQuestions: newCheckInQuestionList,
          checkInQuestionDefinition: newCheckInQuestionDefinition,
          createdAt: new Date(),
          lastUpdatedAt: new Date(),
          lastUpdatedBy: loggedInUserId,
          coachId,
          tag: selectedTag,
        };
        await AppCustomCheckin.addDoc(newCheckInQuestionTemplate);
      }
      if (isComponentMountedRef.current) {
        setIsModalOpen(false);
        setIsLoading(false);
      }
    }
  }, [
    updateCheckInDoc,
    checkInQuestionList,
    checkInQuestionDefinition,
    showToast,
    selectedCheckinDoc,
    coachId,
    selectedTag,
    loggedInUserId,
    isComponentMountedRef,
  ]);

  // move position of array element after drag and drop
  const move = (array, from, to) => {
    array.splice(to, 0, array.splice(from, 1)[0]);
    return [...array];
  };

  const onDrop = useCallback(({ removedIndex, addedIndex }) => {
    const updatedRemovedIndex = removedIndex;
    const updatedAddedIndex = addedIndex;
    if (checkInQuestionList) {
      const newArray = move([...checkInQuestionList], updatedRemovedIndex, updatedAddedIndex);
      updateCheckInDoc(newArray, checkInQuestionDefinition);
    }
  }, [
    updateCheckInDoc,
    checkInQuestionList,
    checkInQuestionDefinition,
  ]);

  const handleDelete = useCallback((removedIndex) => {
    if (checkInQuestionList && checkInQuestionDefinition) {
      const newArray = [...checkInQuestionList];
      const removedCheckInQuestion = newArray.splice(removedIndex, 1)[0];

      const newDefinition = { ...checkInQuestionDefinition };
      delete newDefinition[removedCheckInQuestion];
      updateCheckInDoc(newArray, newDefinition);
    }
  }, [
    updateCheckInDoc,
    checkInQuestionList,
    checkInQuestionDefinition,
  ]);

  const handleEdit = useCallback((editedIndex, editedQuestion, editedPrompt) => {
    if (checkInQuestionList && checkInQuestionDefinition) {
      const editedCheckInQuestionId = checkInQuestionList[editedIndex];
      checkInQuestionDefinition[editedCheckInQuestionId].question = editedQuestion;
      checkInQuestionDefinition[editedCheckInQuestionId].prompt = editedPrompt;
      const newDefinition = { ...checkInQuestionDefinition };
      updateCheckInDoc(checkInQuestionList, newDefinition);
    }
  }, [
    updateCheckInDoc,
    checkInQuestionList,
    checkInQuestionDefinition,
  ]);

  const handleHide = useCallback((hiddenIndex) => {
    if (checkInQuestionList && checkInQuestionDefinition) {
      const hiddenCheckInQuestionId = checkInQuestionList[hiddenIndex];
      checkInQuestionDefinition[hiddenCheckInQuestionId].active = !checkInQuestionDefinition[hiddenCheckInQuestionId]
        .active;
      const newDefinition = { ...checkInQuestionDefinition };
      updateCheckInDoc(checkInQuestionList, newDefinition);
    }
  }, [
    updateCheckInDoc,
    checkInQuestionList,
    checkInQuestionDefinition,
  ]);

  const handleCheckInDayClick = useCallback(async () => {
    setIsLoading(true);
    defaultCheckinDoc.setCheckInDay(newCheckInDay);
    defaultCheckinDoc.setCheckInDayUpdatedBy(coachName);
    if (isComponentMountedRef.current) {
      setIsLoading(false);
      setIsConfirmDialogOpen(false);
    }
  }, [
    isComponentMountedRef,
    newCheckInDay,
    coachName,
    defaultCheckinDoc,
  ]);

  const handleImageUploadRequirementClick = useCallback(async (isRequired) => {
    setIsLoading(true);
    defaultCheckinDoc.setImageUploadRequirement(isRequired, loggedInUserId);
    if (isComponentMountedRef.current) {
      setIsLoading(false);
    }
  }, [
    isComponentMountedRef,
    defaultCheckinDoc,
    loggedInUserId,
  ]);

  return (
    <Container>
      <ConfigContainer>
        <HeaderContainer>
          <TitleContainer>
            <Title>{texts.checkInDay.title}</Title>
            <SubTitle>{texts.checkInDay.description}</SubTitle>
          </TitleContainer>
        </HeaderContainer>
        <CheckInDayContainer>
          {moment.weekdaysShort().map((day, index) => (
            <CheckInDay
              key={day}
              onClick={() => {
                setNewCheckInDay(index);
                setIsConfirmDialogOpen(true);
              }}
              isSelected={Object.keys(userCountByCheckInDay).includes(String(index))
                || (customCheckInDay === index)}
            >
              {day}
              {userCountByCheckInDay[index] && (
                <ClientCount>
                  {`${userCountByCheckInDay[index]} ${texts.clients}`}
                </ClientCount>
              )}
              {(customCheckInDay === index) && <StyledStarIcon />}
            </CheckInDay>
          ))}
        </CheckInDayContainer>
        <CheckInDayContainer>
          <CheckInDayLabel>
            {ReactHtmlParser(format(texts.checkInDayLabel,
              {
                checkInDay: moment.weekdays()[customCheckInDay],
                updatedBy: checkInDayUpdatedby,
                updatedAt: formattedCheckInDayUpdatedAt,
              }))}
          </CheckInDayLabel>
        </CheckInDayContainer>
      </ConfigContainer>
      <ConfigContainer>
        <HeaderContainer>
          <TitleContainer>
            <Title>{texts.checkInImages.title}</Title>
            <SubTitle>{texts.checkInImages.description}</SubTitle>
          </TitleContainer>
        </HeaderContainer>
        <ImageUploadOptionsContainer>
          <StyledRadioButtonGroup
            options={[
              {
                label: texts.mandatoryText,
                value: texts.mandatory,
              }, {
                label: texts.optionalText,
                value: texts.optional,
              },
            ]}
            selectedOption={isImageUploadCompulsory ? texts.mandatory : texts.optional}
            onOptionChange={(value) => handleImageUploadRequirementClick(value === texts.mandatory)}
          />
        </ImageUploadOptionsContainer>
        <CheckInDayContainer>
          <CheckInDayLabel>
            {texts.checkInImageUpload}
          </CheckInDayLabel>
          <CheckInDayText>
            {isImageUploadCompulsory ? texts.mandatory : texts.optional}
          </CheckInDayText>
        </CheckInDayContainer>
      </ConfigContainer>
      <ConfigContainer>
        <HeaderContainer>
          <TitleContainer>
            <Title>{texts.checkInQuestions.title}</Title>
            <SubTitle>{texts.checkInQuestions.description}</SubTitle>
          </TitleContainer>
        </HeaderContainer>
        <StyledSelectField
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
        >
          {clientTagsList.map((tag) => (
            <MenuItem key={tag.id} value={tag.value}>
              {tag.label}
            </MenuItem>
          ))}
        </StyledSelectField>
        <Alert severity="info">{texts.warningMessage}</Alert>
        <StyledList>
          <DraggableContainer dragHandleSelector=".drag-handle" lockAxis="y" onDrop={(data) => onDrop(data, false)}>
            {checkInQuestionList.length > 0 ? (
              checkInQuestionList.map((checkInQuestionId, index) => {
                const checkInQuestion = checkInQuestionDefinition?.[checkInQuestionId];
                return (
                  <CheckInItem
                    index={index}
                    handleDelete={handleDelete}
                    question={checkInQuestion?.question}
                    prompt={checkInQuestion?.prompt}
                    key={checkInQuestionId}
                    handleEdit={handleEdit}
                    handleHide={handleHide}
                    isHidden={!(checkInQuestion?.active)}
                  />
                );
              })
            ) : (
              <EmptyView content={selectedTag === DEFAULT_TAG ? texts.noDefaultQuestions : texts.noCheckInQuestions} />
            )}
          </DraggableContainer>
        </StyledList>
        <StyledButton
          variant="contained"
          onClick={() => setIsModalOpen(true)}
        >
          <StyledQuestionIcon />
          {texts.addNewQuestion}
        </StyledButton>
        <QuestionInputModal
          isOpen={isModalOpen}
          dialogTexts={{
            title: texts.dialogTitle,
            content: texts.dialogContent,
          }}
          onCancel={() => setIsModalOpen(false)}
          onConfirm={addNewQuestion}
        />
        <ConfirmDialog
          isOpen={isConfirmDialogOpen}
          onConfirm={() => handleCheckInDayClick()}
          onCancel={() => setIsConfirmDialogOpen(false)}
          dialogTexts={{
            title: texts.confirmCheckInDayChange,
            content: <CheckInDayConfirmContent
              currentDay={moment.weekdays(customCheckInDay)}
              newDay={moment.weekdays(newCheckInDay)}
            />,
          }}
        />
      </ConfigContainer>
      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

export default compose(
  withCustomizationContextReady,
  observer,
)(CheckInCustomization);
